import { type GridElement } from '..';
import { type Config } from '../../../lib/config';
import { generateTransformHandle } from '../../../lib/selection';
import { type RuntimeState } from '../../../stores/useEditorRuntimeStore';
import { type BoundsWithCenter } from '../../../utils/editor/bounds';
import { type Point } from '../../../utils/math';
import { getPathFromPoints } from '../../poly/utils/path';
import { getPolyTransformHandles } from '../../poly/utils/transform';

export const getGridBounds = (element: GridElement): BoundsWithCenter => {
  const allPoints = [...element.points];
  element.cornerLines?.forEach((line) => {
    allPoints.push(line[0], line[1]);
  });
  const xs = allPoints.map((p) => p[0]);
  const ys = allPoints.map((p) => p[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return [
    minX + element.x,
    minY + element.y,
    maxX + element.x,
    maxY + element.y,
    (minX + maxX) / 2 + element.x,
    (minY + maxY) / 2 + element.y,
  ];
};

export const getGridTransformHandles = (
  grid: GridElement,
  { state, config }: { config: Config; state: RuntimeState },
) => {
  if (!grid.cornerLines?.length) {
    return getPolyTransformHandles(grid, { state, config });
  }

  const size = 10;
  const handleSize = size / state.zoom.value;
  const offset = -handleSize / 2;

  const handles = getPolyTransformHandles(grid, { state, config });
  const [, , , , centerX, centerY] = getGridBounds(grid);

  grid.cornerLines.forEach((line, lineIndex) => {
    line.forEach((point, pointIndex) => {
      const x = grid.x + point[0] + offset;
      const y = grid.y + point[1] + offset;
      const bounds = generateTransformHandle(
        x,
        y,
        handleSize,
        handleSize,
        centerX,
        centerY,
        grid.angle,
      );
      handles[`cornerLine-${lineIndex}-${pointIndex}`] = bounds;
    });
  });

  return handles;
};

const offsetPointsLocal = (pts: Point[], ox: number, oy: number): Point[] =>
  pts.map((p) => [p[0] - ox, p[1] - oy] as Point);

export const normalizeGridToBounds = (
  element: GridElement,
  points: Point[],
  cornerLines?: [Point, Point][],
): {
  x: number;
  y: number;
  width: number;
  height: number;
  points: Point[];
  cornerLines?: [Point, Point][];
} => {
  const all: Point[] = [...points];
  cornerLines?.forEach((l) => {
    all.push(l[0], l[1]);
  });
  const minX = Math.min(...all.map((p) => p[0]));
  const minY = Math.min(...all.map((p) => p[1]));
  const maxX = Math.max(...all.map((p) => p[0]));
  const maxY = Math.max(...all.map((p) => p[1]));
  return {
    points: offsetPointsLocal(points, minX, minY),
    cornerLines: cornerLines?.map(
      (l) => offsetPointsLocal(l, minX, minY) as [Point, Point],
    ),
    x: element.x + minX,
    y: element.y + minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

export const translateGridCorner = (
  pointIndex: number,
  gridElement: GridElement,
  [pointerX, pointerY]: Point,
  [originX, originY]: Point,
): Partial<GridElement> => {
  const dx = pointerX - originX;
  const dy = pointerY - originY;

  const newPoints = gridElement.points.map((p, i) =>
    i === pointIndex
      ? ([p[0] + dx, p[1] + dy] as Point)
      : ([p[0], p[1]] as Point),
  );

  const allPointsForBounds: Point[] = [...newPoints];
  gridElement.cornerLines?.forEach((line) => {
    allPointsForBounds.push(line[0], line[1]);
  });

  const xs = allPointsForBounds.map((p) => p[0]);
  const ys = allPointsForBounds.map((p) => p[1]);
  const smallestX = Math.min(...xs);
  const smallestY = Math.min(...ys);

  const normalizedPoints = offsetPointsLocal(newPoints, smallestX, smallestY);
  const normalizedCornerLines = gridElement.cornerLines?.map(
    (line) => offsetPointsLocal(line, smallestX, smallestY) as [Point, Point],
  );

  const width = Math.max(...xs) - smallestX;
  const height = Math.max(...ys) - smallestY;

  return {
    points: normalizedPoints,
    cornerLines: normalizedCornerLines,
    x: gridElement.x + smallestX,
    y: gridElement.y + smallestY,
    width,
    height,
  };
};

export const getPathFromGrid = (element: GridElement) => {
  let path = getPathFromPoints(element.points, undefined, true);
  if (element.cornerLines?.length) {
    const { points, cornerLines } = element;
    path = new Path2D();
    path.moveTo(points[0][0], points[0][1]);
    for (const [top] of cornerLines) {
      path.lineTo(top[0], top[1]);
    }
    path.lineTo(points[1][0], points[1][1]);
    path.lineTo(points[2][0], points[2][1]);
    for (let i = cornerLines.length - 1; i >= 0; i--) {
      path.lineTo(cornerLines[i][1][0], cornerLines[i][1][1]);
    }
    path.lineTo(points[3][0], points[3][1]);
    path.closePath();
  }
  return path;
};
